import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ChatHeader } from "./ChatHeader";
import { ChatMessages } from "./ChatMessages";
import { ChatInput } from "./ChatInput";
import { ConversationsList } from "./ConversationsList";
import { AboutPanel } from "./AboutPanel";
import { useChat } from "@/hooks/useChat";
import { WIDGET_VERSION, MCP_LOGIN_STORAGE_KEY } from "@/config/security";
import { getEncrypted } from "@/services/core/cryptoStorage";

export const ChatContainer = ({
  gpcService,
  licenseToken,
  widgetConfig,
  onClose,
  themeMode,
  onToggleTheme,
  showBranding = true,
  showMetadata = false,
  onError,
  version = WIDGET_VERSION,
  onRequestMcpLogin,
  mcpLoggedIn = false,
}) => {
  const { t } = useTranslation();
  const [showConversations, setShowConversations] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [tiaCoreLoading, setTiaCoreLoading] = useState(false);

  const mcpEnabled = widgetConfig?.mcp?.enabled && widgetConfig?.mcp?.auth_mode === "credentials";
  const tiaCoreEnabled = !!(
    widgetConfig?.widget_customization?.tia_core_enabled ||
    widgetConfig?.widget_customization?.tia_core_url_backend ||
    widgetConfig?.widget_customization?.tia_core_url_frontend
  );
  const theme = themeMode;
  const toggleTheme = onToggleTheme;

  const handleSessionExpired = useCallback(() => {}, []);

  const {
    messages,
    isLoading,
    sendMessage,
    currentConversationId,
    conversations,
    totalConversations,
    isLoadingHistory,
    startNewConversation,
    switchConversation,
    renameConversation,
    deleteConversation,
    isRestoringConversation,
    hasMoreConversations,
    isLoadingMoreConversations,
    loadMoreConversations,
    hasMoreMessages,
    isLoadingMoreMessages,
    loadMoreMessages,
  } = useChat(gpcService, handleSessionExpired);

  const handleToggleConversations = useCallback(() => {
    setShowConversations((prev) => !prev);
    setShowAbout(false);
  }, []);

  const handleToggleAbout = useCallback(() => {
    setShowAbout((prev) => !prev);
    setShowConversations(false);
  }, []);

  const handleToggleMcpLogin = useCallback(() => {
    onRequestMcpLogin?.(mcpLoggedIn ? "logout" : "login");
  }, [mcpLoggedIn, onRequestMcpLogin]);

  const handleOpenTiaCore = useCallback(async () => {
    console.log("[TIA Core] Click. enabled:", tiaCoreEnabled, "mcpLoggedIn:", mcpLoggedIn);
    if (!tiaCoreEnabled || !mcpLoggedIn) return;

    setTiaCoreLoading(true);
    try {
      const mcpData = await getEncrypted(MCP_LOGIN_STORAGE_KEY);
      console.log("[TIA Core] mcpData present:", !!mcpData);
      if (!mcpData) return;

      let email = mcpData.username || mcpData.email || "";
      if (email && !/^[^@]+@[^@]+\.[a-zA-Z]{2,}$/.test(email)) {
        email = `${email.replace(/@.*$/, "")}@gmail.com`;
      }

      const payload = {
        email,
        password: mcpData.password,
      };

      console.log("[TIA Core] Calling /tia-core/login with email:", email);
      const response = await gpcService.httpClient.post("/tia-core/login", payload);
      console.log("[TIA Core] Response:", response);

      if (response.success && response.data?.redirect_url) {
        console.log("[TIA Core] Redirecting to:", response.data.redirect_url);
        window.open(response.data.redirect_url, "_blank");
      } else {
        console.warn("[TIA Core] No redirect_url in response:", response);
      }
    } catch (err) {
      console.error("[TIA Core] Error:", err);
      try {
        const parsed = JSON.parse(err.message);
        console.error("[TIA Core] Parsed error:", parsed);
      } catch (_) {}
    } finally {
      setTiaCoreLoading(false);
    }
  }, [tiaCoreEnabled, mcpLoggedIn, gpcService]);

  const handleSelectConversation = useCallback(
    (conversationId) => {
      switchConversation(conversationId);
      setShowConversations(false);
    },
    [switchConversation],
  );

  const handleNewConversation = useCallback(() => {
    startNewConversation();
    setShowConversations(false);
  }, [startNewConversation]);

  useEffect(() => {
    return () => {
      if (gpcService) {
        gpcService.releaseSession().catch((err) => {
          console.error("Failed to release session:", err);
        });
      }
    };
  }, [gpcService]);

  const chatHeader = (
    <ChatHeader
      onClose={onClose}
      theme={theme}
      onToggleTheme={toggleTheme}
      showBranding={showBranding}
      widgetConfig={widgetConfig}
      onToggleConversations={handleToggleConversations}
      showConversations={showConversations}
      conversationsCount={totalConversations}
      onToggleAbout={handleToggleAbout}
      showAbout={showAbout}
      version={version}
      onToggleMcpLogin={mcpEnabled ? handleToggleMcpLogin : undefined}
      showMcpLogin={false}
      mcpLoggedIn={mcpLoggedIn}
      onOpenTiaCore={handleOpenTiaCore}
      tiaCoreLoading={tiaCoreLoading}
      showTiaCoreButton={tiaCoreEnabled}
    />
  );

  if (showConversations || showAbout) {
    return (
      <div className={`gpc-chat-window gpc-animate-fade-in ${theme === "dark" ? "gpc-dark" : ""}`}>
        {chatHeader}
        <div className="gpc-conversations-overlay-body">
          {showConversations && (
            <ConversationsList
              conversations={conversations}
              totalConversations={totalConversations}
              currentConversationId={currentConversationId}
              onSelectConversation={handleSelectConversation}
              onNewConversation={handleNewConversation}
              onRenameConversation={renameConversation}
              onDeleteConversation={deleteConversation}
              isLoadingHistory={isLoadingHistory}
              hasMoreConversations={hasMoreConversations}
              isLoadingMoreConversations={isLoadingMoreConversations}
              onLoadMore={loadMoreConversations}
            />
          )}
          {showAbout && <AboutPanel version={version} />}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`gpc-chat-window gpc-animate-fade-in ${theme === "dark" ? "gpc-dark" : ""}`}
    >
      {chatHeader}
      <ChatMessages
        messages={messages}
        isLoading={isLoading || isLoadingHistory || isRestoringConversation}
        showMetadata={showMetadata}
        widgetConfig={widgetConfig}
        hasMoreMessages={hasMoreMessages}
        isLoadingMoreMessages={isLoadingMoreMessages}
        onLoadMoreMessages={loadMoreMessages}
      />
      <ChatInput
        onSendMessage={sendMessage}
        isLoading={isLoading || isRestoringConversation}
        disabled={isRestoringConversation}
        gpcService={gpcService}
        showBranding={showBranding}
      />
    </div>
  );
};
